import React, { useEffect, useState } from 'react';
import { Box, TextField, Modal, FormControl, MenuItem, Select, Button, IconButton, Autocomplete, Typography, InputAdornment } from '@mui/material';
import * as Yup from 'yup';
import ModalContent from '../../../common/components/Modal';
import { useFormik } from 'formik';
import { useQuery } from 'react-query';
import { CompanyApi } from '../../../services/phpServices';
import { AssetApi, AssetDto } from '../../../services/netServices';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { CustomListItem } from '../../checkList/components/ListItem';
import { GlobalErrorBox } from '../../../common/components/ErrorBox';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { NumericFormat } from 'react-number-format';
import { toast } from 'react-toastify';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AssetDetailModal from './AssetDetailModal';
import CloseIcon from '@mui/icons-material/Close';
import CateModal from './CateModal';
import AddIcon from '@mui/icons-material/Add';

interface AssetModalProps {
  open: boolean;
  onClose: () => void;
  refetchGetListAsset?: any
  asset: AssetDto | undefined
  isEditing: boolean
}
export enum AssetCondition {
  Good = "good",
  New = "new",
  Fair = "fair",
  NeedRepair = "need_repair",
}
export default function AssetModal({ open, onClose, refetchGetListAsset, asset, isEditing }: AssetModalProps): JSX.Element {
  const [isLoading, setIsLoading] = React.useState(false);
  const { t } = useTranslation() as any
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [openNewCate, setOpenNewCate] = React.useState<boolean>(false)

  const [assetDetailModal, setAssetDetailModal] = useState<{ visible: boolean; id?: number }>({
    visible: false,
    id: undefined,
  });
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [inputValue, setInputValue] = React.useState("")
  const resizeImage = (file: File, maxWidth: number, maxHeight: number): Promise<File> => {
    return new Promise((resolve) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => {
        if (!e.target?.result) return;

        img.src = e.target.result as string;
      };

      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > maxWidth || height > maxHeight) {
          if (width > height) {
            height *= maxWidth / width;
            width = maxWidth;
          } else {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          if (blob) {
            const resizedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            });
            resolve(resizedFile);
          }
        }, file.type);
      };

      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    const resizedFile = await resizeImage(selected, 800, 600)
    setFile(resizedFile);
    setPreviewUrl(URL.createObjectURL(resizedFile));
    formik.setFieldValue('File', resizedFile);
  };
  const { data, refetch } = useQuery(
    ['/Asset/Category'],
    async () => {
      const dataLocalStorage = localStorage.getItem('USER')
      const parsedData = JSON.parse(dataLocalStorage ?? '')
      const token = parsedData?.User?.token
      const { data } = await new AssetApi().assetCategoryGet(token);
      return data.Data
    },
    { enabled: false }
  );
  const { data: dataTags, refetch: refetchTags } = useQuery(
    ['/Asset/Tags'],
    async () => {
      const dataLocalStorage = localStorage.getItem('USER')
      const parsedData = JSON.parse(dataLocalStorage ?? '')
      const token = parsedData?.User?.token
      const { data } = await new AssetApi().assetTagsGet(token)
      return data.Data
    },
    { enabled: false }
  );
  const { data: assetById, refetch: refetchAssetById } = useQuery(
    ['/Asset/{id}'],
    async () => {
      const { data } = await new AssetApi().assetIdGet(asset?.Id ?? 0);
      return data;
    },
    { enabled: false }
  );
  useEffect(() => {
    if (open) {
      refetch()
      refetchTags()
      if (asset?.Id) refetchAssetById()
    }
  }, [open, asset?.Id, refetch, refetchAssetById]);

  useEffect(() => {
    if (isEditing && assetById?.Image && !file) {
      setPreviewUrl(assetById.Image);
    }
  }, [isEditing, assetById, file]);
  const [initialValues, setInitialValues] = React.useState<AssetDto>({
    Name: '',
    CategoryId: undefined,
    Description: '',
    Unit: '',
    Quantity: 1,
    SerialNumber: '',
    Brand: '',
    PurchaseDate: dayjs().unix(),
    Tags: [],
    Tag: '',
    Image: '',
    Area: '',
  });

  React.useEffect(() => {
    if (asset?.Id && assetById) {
      setInitialValues({
        Name: assetById.Name ?? '',
        CategoryId: assetById.CategoryId ?? null,
        Description: assetById.Description ?? '',
        Unit: assetById.Unit ?? '',
        Quantity: assetById.Quantity ?? 0,
        SerialNumber: assetById.SerialNumber ?? '',
        Brand: assetById.Brand ?? '',
        PurchaseDate: assetById.PurchaseDate ?? 0,
        Tags: assetById.Tags ?? [],
        Tag: assetById.Tag ?? '',
        Image: assetById.Image ?? '',
        Area: assetById.Area ?? '',
      });
    }
  }, [asset?.Id, assetById]);


  const validationSchema = Yup.object({
    Name: Yup.string().required(t('34_00_name_required')),
    Quantity: isEditing
      ? Yup.number().notRequired()
      : Yup.number()
        .required(t('34_00_quantity_required'))
        .min(1, t('34_00_quantity_required')),


    // PurchasePrice: Yup.string().required(t('34_00_purchase_price_required')),

  });
  const handleSubmit = async (values: AssetDto): Promise<void> => {
    try {
      setIsLoading(true)
      const timestamp = typeof values.PurchaseDate === 'number' ? values.PurchaseDate : 0;
      const dataLocalStorage = localStorage.getItem('USER')
      const parsedData = JSON.parse(dataLocalStorage ?? '')
      const token = parsedData?.User?.token
      if (asset?.Id != null) {
        await new AssetApi().assetPut(
          token,
          asset?.Id ?? 0,
          values?.Name || undefined,
          values?.CategoryId || undefined,
          values?.Category || undefined,
          values?.Description || undefined,
          values?.Unit || undefined,
          values?.Quantity ?? undefined,
          values?.SerialNumber || undefined,
          values?.Brand || undefined,
          timestamp || undefined,
          values?.PurchasePrice || undefined,
          values?.Supplier || undefined,
          values?.WarrantyMonths ?? undefined,
          values?.Tags && values.Tags.length > 0 ? values.Tags : undefined,
          values?.Tag && values.Tag.trim() !== "" ? values.Tag : undefined,
          values?.IsAssigned ?? undefined,
          values?.Assignees && values.Assignees.length > 0 ? values.Assignees : undefined,
          values?.File || undefined,
          values?.Image || undefined,
          values?.Area || undefined
        );

        onClose();
        toast.success(t("00_02_success_saved"));
        refetchGetListAsset();
      } else {
        await new AssetApi().assetPost(
          token,
          0,
          values?.Name || undefined,
          values?.CategoryId || undefined,
          values?.Category || undefined,
          values?.Description || undefined,
          values?.Unit || undefined,
          values?.Quantity ?? undefined,
          values?.SerialNumber || undefined,
          values?.Brand || undefined,
          timestamp || undefined,
          values?.PurchasePrice || undefined,
          values?.Supplier || undefined,
          values?.WarrantyMonths ?? undefined,
          values?.Tags && values.Tags.length > 0 ? values.Tags : undefined,
          values?.Tag && values.Tag.trim() !== "" ? values.Tag : undefined,
          values?.IsAssigned ?? undefined,
          values?.Assignees && values.Assignees.length > 0 ? values.Assignees : undefined,
          values?.File || undefined,
          values?.Image || undefined,
          values?.Area || undefined
        );

        onClose();
        toast.success(t("00_02_success_saved"));
        refetchGetListAsset();
      }

    } catch (ex: any) {
      const errorMessage = ex?.response?.data;

      if (errorMessage === 'brand_existed') {
        const result = await new AssetApi().assetGetByCodeGet(values?.Brand);
        const assetId = result.data?.Id;
        if (assetId) {
          setAssetDetailModal({ visible: true, id: assetId });
        }

      } else if (errorMessage === 'serial_number_existed') {
        const result = await new AssetApi().assetGetByCodeGet(values?.SerialNumber);
        const assetId = result.data?.Id;
        if (assetId) {
          setAssetDetailModal({ visible: true, id: assetId });
        }

      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }


  }



  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: handleSubmit,
    validationSchema,
  });
  const options = data?.flatMap(parent => {
    if (parent.Childs && parent.Childs.length > 0) {
      // Có con → return cha + từng con
      return [
        { ...parent, isParent: true, parentName: null },
        ...parent.Childs.map(child => ({
          ...child,
          isParent: false,
          parentName: parent.Name
        }))
      ];
    }
    // Không có con → return chính cha
    return [{ ...parent, isParent: true, parentName: null }];
  }) ?? [];

  return (
    <Modal open={open} onClose={onClose}>
      <Box>
        <ModalContent onClose={onClose} title={!isEditing ? t('34_00_new_asset') : t('34_00_edit_asset')} width={600}>
          <form onSubmit={formik.handleSubmit}>
            {formik.submitCount > 0 && Object.keys(formik.errors).length > 0 && (
              <GlobalErrorBox
                errors={Object.values(formik.errors).map((e) => e?.toString() ?? '')}
              />
            )}
            <Box>
              <CustomListItem
                leftContainerWidth={150}
                text={t('34_00_asset_name')}
                RightComponent={
                  <TextField
                    variant="standard"
                    fullWidth
                    value={formik.values.Name}
                    onChange={(e) => formik.setFieldValue('Name', e.target.value)}

                  />
                }
              />

            </Box>
            <Box mt={4}>
              <CustomListItem
                leftContainerWidth={150}
                text={t('13_05_category')}
                containerStyle={{ marginBottom: '16px' }}
                RightComponent={
                  <Autocomplete
                    value={options.find(opt => opt.Id === formik.values.CategoryId) ?? null}
                    options={options}
                    noOptionsText={t('00_06_no_data')}
                    getOptionLabel={(option) => option.Name}
                    getOptionDisabled={(option) => option.isParent && option.Childs?.length > 0}
                    onChange={(event, newValue) => {
                      if (!newValue) return;
                      if (newValue.isParent && newValue.Childs?.length > 0) return;
                      formik.setFieldValue('CategoryId', newValue?.Id ?? null);
                    }}
                    fullWidth
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        fullWidth
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {params.InputProps.endAdornment}
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="add-category"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenNewCate(true);
                                  }}
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            </>
                          )
                        }}
                      />
                    )}
                    renderOption={(props, option) => {
                      const isDisabled = option.isParent && option.Childs?.length > 0;
                      return (
                        <li
                          {...props}
                          key={option.Id}
                          aria-disabled={isDisabled}
                          style={{
                            ...(option.isParent
                              ? {
                                fontSize: '14px',
                                textTransform: 'uppercase',
                                fontWeight: 500,
                                color: 'rgba(0,0,0,0.54)',
                                padding: '16px',
                                pointerEvents: isDisabled ? 'none' : 'auto',
                              }
                              : {
                                paddingLeft: '32px',
                                color: 'rgba(0,0,0,0.87)',
                                height: '48px',
                                pointerEvents: isDisabled ? 'none' : 'auto',
                              }),
                          }}
                        >
                          {option.Name}
                        </li>
                      );
                    }}
                  />


                }

              />
            </Box>

            <Box mt={4}>
              <CustomListItem
                leftContainerWidth={150}
                text={t('20_16_description')}
                RightComponent={
                  <TextField
                    value={formik.values.Description}
                    onChange={(e) => formik.setFieldValue('Description', e.target.value)}
                    fullWidth
                    multiline
                    rows={2}
                    InputProps={{
                      sx: {
                        padding: 0,
                        '& .MuiInputBase-inputMultiline': {
                          padding: '8px',
                        },
                      },
                    }}
                  />
                }
              />
            </Box>
            <Box mt={4}>
              <CustomListItem
                leftContainerWidth={150}
                text={t('00_06_unit')}
                RightComponent={
                  <TextField
                    variant="standard"
                    value={formik.values.Unit}
                    onChange={(e) => formik.setFieldValue('Unit', e.target.value)}
                    fullWidth

                  />
                }
              />
            </Box>
            <Box mt={4}>
              <CustomListItem
                leftContainerWidth={150}
                text={t('00_06_quantity')}
                RightComponent={
                  <NumericFormat
                    customInput={TextField}
                    variant="standard"
                    thousandSeparator=","
                    allowNegative={false}
                    value={formik.values.Quantity}
                    onValueChange={(values) => {
                      const { floatValue } = values;
                      formik.setFieldValue('Quantity', floatValue ?? '');
                    }}
                    allowLeadingZeros={false}
                    disabled={isEditing}
                    fullWidth
                    inputProps={{ min: 1 }}
                  />

                }
              />
            </Box>

            <Box mt={4}>
              <CustomListItem
                leftContainerWidth={150}
                text={t('34_00_serial_number')}
                RightComponent={
                  <TextField
                    variant="standard"
                    value={formik.values.SerialNumber}
                    onChange={(e) => formik.setFieldValue('SerialNumber', String(e.target.value))}
                    fullWidth

                  />
                }
              />
            </Box>
            <Box mt={4}>
              <CustomListItem
                leftContainerWidth={150}
                text={t('34_00_brand')}
                RightComponent={
                  <TextField
                    variant="standard"
                    value={formik.values.Brand}
                    onChange={(e) => formik.setFieldValue('Brand', e.target.value)}
                    fullWidth

                  />
                }
              />
            </Box>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box mt={4}>
                <CustomListItem
                  leftContainerWidth={150}
                  text={t('34_00_purchase_date')}
                  RightComponent={
                    <DatePicker
                      format="DD/MM/YYYY"
                      value={formik.values.PurchaseDate ? dayjs(formik.values.PurchaseDate * 1000) : null}
                      onChange={(value) => {
                        if (value && dayjs(value).isValid()) {
                          const timestampSeconds = Math.floor(value.set('millisecond', 0).valueOf() / 1000);
                          formik.setFieldValue('PurchaseDate', timestampSeconds);
                        } else {
                          formik.setFieldValue('PurchaseDate', 0);
                        }
                      }}
                      slotProps={{
                        textField: {
                          variant: 'standard',
                          fullWidth: true,
                          sx: {
                            '& .MuiIconButton-root': {
                              marginRight: '-4px',
                            },
                          },
                        },
                      }}
                    />
                  }
                />
              </Box>
            </LocalizationProvider>


            <Box mt={4} mb={5}>
              <CustomListItem
                leftContainerWidth={150}
                text={
                  t('34_00_area')
                }
                containerStyle={{ marginBottom: '16px' }}
                RightComponent={
                  <TextField
                    value={formik.values.Area}
                    onChange={(e) => formik.setFieldValue('Area', e.target.value)}
                    variant="standard"
                    fullWidth

                  />
                }
              />
            </Box>
            <Box mt={4}>
              <CustomListItem
                leftContainerWidth={150}
                text={t('34_00_tag')}
                RightComponent={
                  <Autocomplete
                    multiple
                    freeSolo
                    sx={{ flex: 1 }}
                    fullWidth
                    options={(dataTags ?? []).map(tag => tag.Name ?? '').filter(Boolean)}
                    getOptionLabel={(option) => option}
                    value={formik.values.Tags ?? []}
                    inputValue={inputValue}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === "input") {
                        setInputValue(newInputValue)
                      }
                    }}
                    onBlur={() => {
                      if (inputValue.trim() !== "") {
                        const newTags = [...formik.values.Tags, inputValue.trim()]
                        const unique = Array.from(new Set(newTags))
                        formik.setFieldValue("Tags", unique)
                        setInputValue("") // reset input
                      }
                    }}
                    onChange={(event, newValue) => {
                      const uniqueValues = Array.from(new Set(newValue))
                      formik.setFieldValue("Tags", uniqueValues)
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        key={params.id}
                        variant="standard"
                      />
                    )}
                  />


                }
              />
            </Box>


            <Box mt={4} mb={5}>
              <CustomListItem
                leftContainerWidth={150}
                text={t('34_00_upload_photo')}
                RightComponent={
                  <Box width="100%">
                    <Box
                      component="label"
                      htmlFor="file-upload"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'end',
                        borderRadius: 1,
                        borderBottom: '1px solid rgba(0,0,0,0.12)',
                        padding: 1
                      }}
                    >
                      {/* <Typography
                        variant="body1"
                        color={file ? 'text.primary' : 'text.secondary'}
                        noWrap
                      >
                        {file?.name ?? 'No file chosen'}
                      </Typography> */}
                      <UploadFileIcon fontSize="medium" color="action" />
                    </Box>
                    <input
                      type="file"
                      id="file-upload"
                      hidden
                      ref={fileInputRef}
                      onChange={handleFileChange}
                    />

                    {previewUrl && previewUrl !== 'null' ? (
                      <Box
                        mt={2}
                        position="relative"
                        display="inline-block"
                        sx={{
                          borderRadius: 1,
                          border: '1px solid #ccc',
                          maxWidth: 400,
                          maxHeight: 300,
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          component="img"
                          src={previewUrl}
                          alt="preview"
                          sx={{
                            width: '100%',
                            height: '150px',
                            objectFit: 'contain',
                            display: 'block',
                          }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                        <IconButton
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            backgroundColor: 'rgba(255,255,255,0.8)',
                            '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
                          }}
                          onClick={() => {
                            setPreviewUrl(null);
                            setFile(null);
                            formik.setFieldValue('File', null);
                            formik.setFieldValue('Image', null);
                            if (fileInputRef.current) {
                              fileInputRef.current.value = '';
                            }
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : null}


                  </Box>
                }
              />
            </Box>

            <Box display="flex" justifyContent="end" gap={4}>
              <Button variant='outlined' style={{ borderColor: '#e5e5e5', color: '#000' }} onClick={onClose}>{t('00_03_cancel')}</Button>
              <Button variant="contained" style={{ color: '#fff', backgroundColor: '#08B0A0' }} disabled={isLoading} type="submit">
                {t('00_03_save')}
              </Button>
            </Box>
          </form>
        </ModalContent>
        {openNewCate &&
          <CateModal open={openNewCate} onClose={() => { setOpenNewCate(false) }} onSuccess={() => {
            refetch();
          }} />
        }
        <AssetDetailModal
          open={assetDetailModal.visible}
          onClose={() => setAssetDetailModal({ visible: false, id: undefined })}
          id={assetDetailModal.id}
        />
      </Box>
    </Modal>
  );
}